import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { api } from "../../lib/api";
import { toast } from "sonner";
import {
  Copy,
  ArrowSquareOut,
  Phone,
  Buildings,
  ChatText,
  ShieldCheck,
  CheckCircle,
  Circle,
  Warning,
} from "@phosphor-icons/react";

// Read-only walkthrough that surfaces exactly what to paste into each rejected
// field on the Twilio Toll-Free Verification form. Pre-fills the Use Case
// Summary, opt-in flow, sample message, and frequency — the four boxes Twilio
// rejects most often. Every block has its own "Copy" button so the admin can
// paste straight into the Twilio Console without retyping anything.
//
// Auto-templates the live LLC name + EIN from /admin/business-identity once
// they're saved, so the operator doesn't have to remember to swap placeholders
// after Atlas delivers the certificate.
export default function TwilioTfvGuide() {
  const [tfv, setTfv] = useState(null);
  const [bi, setBi] = useState(null);
  const [biLoaded, setBiLoaded] = useState(false);

  useEffect(() => {
    api.get("/admin/twilio-tfv-status")
      .then((r) => setTfv(r.data))
      .catch(() => setTfv(null));
    api.get("/admin/business-identity")
      .then((r) => setBi(r.data?.identity || {}))
      .catch(() => setBi({}))
      .finally(() => setBiLoaded(true));
  }, []);

  const copy = (label, text) => {
    navigator.clipboard.writeText(text).then(
      () => toast.success(`Copied: ${label}`, { duration: 4000 }),
      () => toast.error("Clipboard blocked — please select and copy manually"),
    );
  };

  // ---------- Live values templated from business_identity ----------
  const legalName = bi?.legal_name || "Zeborg Water Solution Corp.";  // pre-LLC placeholder
  const dba = bi?.dba || "Zcardon";
  const isPlaceholderName = !bi?.legal_name;
  const ein = bi?.ein || "";
  const einFormatted = ein.length === 9 ? `${ein.slice(0, 2)}-${ein.slice(2)}` : "(EIN pending)";
  const fullAddress = bi?.address_line1
    ? `${bi.address_line1}${bi.address_line2 ? ", " + bi.address_line2 : ""}, ${bi.address_city || ""}, ${bi.address_state || ""} ${bi.address_postal || ""}`
    : "(address pending — fill /admin/business-identity)";

  // ---------- Prerequisite checklist ----------
  const [domainOk, setDomainOk] = useState(null);
  useEffect(() => {
    // Best-effort: HEAD request to https://www.zcardon.com. CORS may block
    // reading the response but `no-cors` mode at least tells us if the host
    // resolves and returns *something*.
    let cancelled = false;
    (async () => {
      try {
        await fetch("https://www.zcardon.com", { method: "HEAD", mode: "no-cors" });
        if (!cancelled) setDomainOk(true);
      } catch {
        if (!cancelled) setDomainOk(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const prereqs = useMemo(() => [
    {
      key: "llc_name",
      label: "LLC name finalized in business_identity",
      done: !!bi?.legal_name,
      hint: bi?.legal_name
        ? `Using "${bi.legal_name}"`
        : "Atlas certificate not yet pasted — packet still shows the placeholder",
    },
    {
      key: "ein",
      label: "EIN saved",
      done: !!ein && ein.length === 9,
      hint: ein && ein.length === 9
        ? `EIN ${einFormatted} on file`
        : "EIN not yet received from IRS (Atlas obtains it ~7 days after certificate)",
    },
    {
      key: "address",
      label: "US business address saved",
      done: !!(bi?.address_line1 && bi?.address_city && bi?.address_state),
      hint: bi?.address_line1
        ? `Will appear in TFV packet: ${fullAddress}`
        : "Required so the business address on the TFV form matches your LLC documents",
    },
    {
      key: "domain",
      label: "zcardon.com responds publicly",
      done: domainOk === true,
      hint: domainOk === null
        ? "Checking…"
        : domainOk
          ? "DNS resolves. Twilio reviewers will be able to visit the URL."
          : "Domain unreachable. Twilio reviewers click your URL — a 404/timeout = instant reject. Fix DNS + redeploy before resubmitting.",
    },
  ], [bi, ein, einFormatted, fullAddress, domainOk]);

  const allReady = prereqs.every((p) => p.done);

  const fields = [
    {
      id: "use_case",
      label: "Use Case Summary",
      icon: <ChatText size={18} weight="duotone" />,
      hint: "This is the field Twilio rejected on the last submission. Paste verbatim — it covers every detail Twilio's reviewers want.",
      value:
        `${legalName} (operating as "${dba}", a US-registered prepaid card issuer) issues closed-loop virtual prepaid cards on behalf of customers ("senders") to designated recipients ("receivers").

When a sender funds a card through Stripe (after passing KYC), ${dba} sends a one-time MMS to the receiver's phone number — the same phone number the sender explicitly entered during card issuance. The MMS contains:
  (1) the rendered virtual card image (PNG)
  (2) the card last-4 and expiry
  (3) a 24-hour signed link to view the card and pay at participating merchants

No recurring marketing messages. No third-party promotional content. Opt-out keywords (STOP, UNSUBSCRIBE, CANCEL, END, QUIT) are honored automatically by Twilio Advanced Opt-Out, and HELP returns the support phone number.

Message frequency: 1–2 messages per card issuance event. Average sender issues 1–3 cards per month.

End users (senders) are real, KYC-verified individuals or businesses who explicitly opt-in to send a one-time card delivery message to a recipient they personally identify.`,
    },
    {
      id: "opt_in",
      label: "How do end users consent to receive messages?",
      icon: <ShieldCheck size={18} weight="duotone" />,
      hint: "Twilio wants explicit opt-in language and a screenshot of the consent UI.",
      value:
        `The receiver's phone number is provided directly by the sender at the moment of card creation. The sender confirms a checkbox labeled "I have permission to text this person" before submitting the card-issuance form (see screenshot: customer-facing /dashboard issuance flow at https://www.zcardon.com).

The receiver is also notified at the time of card scan that a one-time message containing their card details was sent on the sender's behalf — and is given a one-tap STOP keyword and a link to https://www.zcardon.com/support to revoke.

No opt-in is collected from third parties or scraped lists. Every recipient number originates from a sender who entered it manually for a real, funded card transaction.`,
    },
    {
      id: "message_sample",
      label: "Sample Message (exact body)",
      icon: <ChatText size={18} weight="duotone" />,
      hint: "Twilio reviewers compare this against your real outbound traffic. Use the exact body the system sends.",
      value:
        `Hi Sarah, Anh sent you a $50 ${dba} card 💳 •••• 4242 Exp 03/27 CVV 123.
View + pay at participating merchants: https://www.zcardon.com/pay?last4=4242&exp=03/27 (link valid 24h).
Reply STOP to opt out · HELP for support.`,
    },
    {
      id: "opt_out",
      label: "Opt-out language",
      icon: <ShieldCheck size={18} weight="duotone" />,
      hint: "Twilio wants you to list the keywords you honour. Twilio Advanced Opt-Out is enabled on the messaging service.",
      value:
        `STOP, UNSUBSCRIBE, CANCEL, END, QUIT — all auto-blocked by Twilio Advanced Opt-Out at the messaging-service level. HELP returns the support phone (+1-866-901-9945) and link to https://www.zcardon.com/support. No further messages are sent to any number that has opted out.`,
    },
    {
      id: "volume",
      label: "Estimated monthly message volume",
      icon: <Phone size={18} weight="duotone" />,
      hint: "Be honest — overstating triggers extra scrutiny. Lowering starting volume reduces risk score on first approval.",
      value: `Estimated 500–2,000 outbound messages per month during launch (one MMS per funded card, ~1–3 cards per active sender). Will request volume increase once initial 90 days of clean traffic is established.`,
    },
    {
      id: "business",
      label: "Business legal name",
      icon: <Buildings size={18} weight="duotone" />,
      hint: "Must match your Twilio account business name AND the LLC certificate exactly.",
      value: legalName,
    },
    {
      id: "ein_field",
      label: "EIN (Tax ID)",
      icon: <Buildings size={18} weight="duotone" />,
      hint: "9-digit IRS Employer Identification Number, formatted XX-XXXXXXX.",
      value: einFormatted,
    },
    {
      id: "address",
      label: "Business address",
      icon: <Buildings size={18} weight="duotone" />,
      hint: "Single-line format Twilio expects. Must match LLC certificate + bank account.",
      value: fullAddress,
    },
    {
      id: "website",
      label: "Production website URL",
      icon: <Buildings size={18} weight="duotone" />,
      hint: "Twilio reviewers click this. If it returns 404 or timeout, the submission is auto-rejected.",
      value: "https://www.zcardon.com",
    },
  ];

  return (
    <div className="min-h-screen bg-[#FBF9F6]">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <Link
          to="/admin/setup"
          className="text-xs tracking-[0.3em] uppercase text-[#4A5C50] link-ul"
          data-testid="tfv-guide-back"
        >
          ← Back to setup checklist
        </Link>

        <header className="mt-6">
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#4A5C50]">
            Twilio Toll-Free Verification
          </p>
          <h1 className="serif text-4xl mt-1">Resubmission packet</h1>
          <p className="text-sm text-[#4A5C50] mt-2 leading-relaxed">
            Twilio rejected the last submission for{" "}
            <span className="mono">"Use Case Summary is Incomplete"</span>. Below is a
            pre-written set of answers tailored to your card-delivery flow.
            Click <span className="font-semibold">Copy</span> beside each field, paste
            into the matching box in the Twilio Console, then resubmit. Approvals
            typically arrive in 5–10 business days.
          </p>
          {tfv?.console_url && (
            <a
              href={tfv.console_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 mt-4 bg-rose-600 hover:bg-rose-700 text-white text-sm rounded-xl px-4 py-2 transition-colors"
              data-testid="tfv-guide-open-twilio"
            >
              Open the rejected submission in Twilio Console{" "}
              <ArrowSquareOut size={14} weight="bold" />
            </a>
          )}
        </header>

        {/* ── Prerequisite checklist ────────────────────────────────────── */}
        {biLoaded && (
          <section
            className={`mt-8 rounded-2xl border p-5 ${
              allReady
                ? "border-emerald-300 bg-emerald-50"
                : "border-amber-300 bg-amber-50"
            }`}
            data-testid="tfv-prereq-checklist"
          >
            <div className="flex items-baseline justify-between gap-3 flex-wrap">
              <h2 className={`serif text-lg ${allReady ? "text-emerald-800" : "text-amber-800"}`}>
                {allReady ? "✓ Ready to resubmit" : "Resubmission prerequisites"}
              </h2>
              <span className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#4A5C50]">
                {prereqs.filter((p) => p.done).length} / {prereqs.length} ready
              </span>
            </div>
            <ul className="mt-3 space-y-2">
              {prereqs.map((p) => (
                <li
                  key={p.key}
                  className="flex items-start gap-2"
                  data-testid={`tfv-prereq-${p.key}`}
                >
                  {p.done ? (
                    <CheckCircle size={16} weight="fill" className="text-emerald-600 mt-0.5 shrink-0" />
                  ) : (
                    <Circle size={16} weight="regular" className="text-amber-500 mt-0.5 shrink-0" />
                  )}
                  <div className="min-w-0">
                    <p className={`text-sm ${p.done ? "text-emerald-900" : "text-amber-900"}`}>
                      {p.label}
                    </p>
                    <p className="text-xs text-[#4A5C50] mt-0.5">{p.hint}</p>
                  </div>
                </li>
              ))}
            </ul>
            {!allReady && (
              <p className="mt-4 text-xs text-amber-900 italic">
                Don't resubmit yet — Twilio penalises rapid re-rejections.
                Wait until the items above are green, then submit once.
              </p>
            )}
          </section>
        )}

        {isPlaceholderName && (
          <div
            className="mt-4 rounded-xl border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 flex items-start gap-2"
            data-testid="tfv-placeholder-warning"
          >
            <Warning size={14} weight="fill" className="shrink-0 mt-0.5 text-amber-600" />
            <p>
              Heads-up: business name below shows the pre-LLC placeholder
              "<strong>Zeborg Water Solution Corp.</strong>" because{" "}
              <Link to="/admin/business-identity" className="underline font-semibold">/admin/business-identity</Link>{" "}
              has no <span className="mono">legal_name</span> yet. Once your Atlas
              certificate arrives, paste your real LLC name there — this page
              auto-refreshes the packet so you can copy/paste without manual edits.
            </p>
          </div>
        )}

        <div className="mt-8 space-y-4">
          {fields.map((f) => (
            <section
              key={f.id}
              className="rounded-2xl border border-[#E5E0D8] bg-white p-5"
              data-testid={`tfv-field-${f.id}`}
            >
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <span className="text-[#184A30]">{f.icon}</span>
                  <h2 className="serif text-lg text-[#112318]">{f.label}</h2>
                </div>
                <Button
                  size="sm"
                  onClick={() => copy(f.label, f.value)}
                  className="bg-[#184A30] hover:bg-[#0F3220] text-white rounded-xl text-xs"
                  data-testid={`tfv-copy-${f.id}`}
                >
                  <Copy size={14} className="mr-1" weight="duotone" /> Copy
                </Button>
              </div>
              <p className="text-xs text-[#4A5C50] mt-1 italic">{f.hint}</p>
              <pre className="mt-3 whitespace-pre-wrap break-words bg-[#FBF9F6] border border-[#E5E0D8] rounded-xl p-3 text-[12px] leading-relaxed text-[#112318] mono">
                {f.value}
              </pre>
            </section>
          ))}
        </div>

        <section className="mt-8 rounded-2xl border border-[#CBA35C] bg-[#CBA35C]/10 p-5">
          <h2 className="serif text-lg text-[#8C6B21]">Reviewer tips</h2>
          <ul className="mt-2 space-y-1.5 text-sm text-[#664E1A] list-disc pl-5">
            <li>
              Upload a screenshot of the customer-facing card-issuance form clearly
              showing the "I have permission to text this person" checkbox.
            </li>
            <li>
              Make sure the business address and EIN on the form match your LLC
              certificate exactly. Mismatches = auto-reject.
            </li>
            <li>
              If Twilio asks for a privacy policy link, use{" "}
              <span className="mono">https://www.zcardon.com/support</span> until
              a dedicated{" "}
              <span className="mono">/privacy</span> page is published.
            </li>
            <li>
              After resubmission, check the banner on the Setup Checklist daily —
              status flips to <span className="font-semibold">IN_REVIEW</span>{" "}
              within a few hours and{" "}
              <span className="font-semibold">TWILIO_APPROVED</span> within 5–10
              business days.
            </li>
          </ul>
        </section>
      </div>
    </div>
  );
}
